"use client";

import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { LinkButton } from "@/components/ui/link-button";
import { Icon } from "@/components/ui/icon";
import { NEUROSCALE_LINKS } from "@/lib/neuroscale";

const ICON = {
  join: "round" as const,
  fill: "outlined" as const,
  radius: "2" as const,
  stroke: "1.5" as const,
};

function generateRequestId(): string {
  return (
    Math.random().toString(36).substring(2, 8).toUpperCase() +
    Math.random().toString(36).substring(2, 6).toUpperCase()
  );
}

export default function NotFound() {
  const router = useRouter();
  const [requestId, setRequestId] = useState<string | undefined>(undefined);

  useEffect(() => {
    setRequestId(generateRequestId());
  }, []);

  return (
    <div className="flex h-svh flex-col overflow-hidden bg-surface-primary">
      <header className="relative flex h-20 shrink-0 items-center justify-center px-6">
        <Image
          src="/logos/neuroscale-full.svg"
          alt="Neuroscale AI"
          width={163}
          height={24}
          className="h-6 w-auto"
        />
      </header>

      <main className="flex flex-1 flex-col items-center justify-center overflow-hidden px-4 sm:px-6">
        <div className="w-full max-w-132">
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-[var(--modal-shadow)]">
            <div className="px-5 pt-6 pb-0 sm:px-8 sm:pt-8">
              <div className="flex flex-col items-center gap-1 text-center">
                <span className="text-sm font-medium leading-5 text-muted-foreground">
                  Status Error
                </span>
                <h1 className="text-2xl font-semibold leading-tight text-heading sm:text-3xl">
                  Page not found.
                </h1>
                <p className="max-w-md text-sm leading-5 text-muted-foreground">
                  The page you were looking for could not be found. Let&apos;s get you back on track.
                </p>
              </div>


              <div className="relative mt-4 h-56 w-full overflow-hidden rounded-xl bg-white/1 pt-6 sm:h-72">
                <Image
                  src="/images/404-image.png"
                  height={1025}
                  width={779}
                  quality={100}
                  alt="404 Not Found Image"
                  className="object-cover object-top"
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 px-5 pt-5 pb-6 sm:px-8 sm:pt-6 sm:pb-8">
              <div className="overflow-hidden rounded-lg border border-border bg-muted shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.2)]">
                <div className="grid grid-cols-2 gap-1.5 p-4">
                  <LinkButton
                    href="https://docs.neuroscale.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="secondary"
                    size="sm"
                    icon={
                      <div className="opacity-70">
                        <Icon name="IconNewspaper1" size={16} className="text-heading" />
                      </div>
                    }
                  >
                    Documentation
                  </LinkButton>
                  <LinkButton
                    href="https://docs.neuroscale.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="secondary"
                    size="sm"
                    icon={
                      <div className="opacity-70">
                        <Icon name="IconGraduateCap" size={16} className="text-heading" />
                      </div>
                    }
                  >
                    Arbi Academy
                  </LinkButton>
                  <LinkButton
                    href="mailto:support@arbi.ai"
                    variant="secondary"
                    size="sm"
                    icon={
                      <div className="opacity-70">
                        <Icon name="IconRescueRing" size={16} className="text-heading" />
                      </div>
                    }
                  >
                    Help Center
                  </LinkButton>
                  <LinkButton
                    href="https://neuroscale.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="secondary"
                    size="sm"
                    icon={
                      <div className="opacity-70">
                        <Icon name="IconGlobe" size={16} className="text-heading" />
                      </div>
                    }
                  >
                    Website
                  </LinkButton>
                </div>
              </div>

              <Button
                onClick={() => router.push("/projects")}
                variant="primary"
                size="sm"
                className="w-full"
                icon={<Icon name="IconHome" size={16} className="text-foreground" />}
              >
                Go Back to Arbi
              </Button>

              <p className="text-center text-xs text-muted-foreground inline-flex items-center gap-1 justify-center">
                Error Code: 404 <span className="opacity-40">&bull;</span> Request ID: {requestId ? <span>{requestId}</span> : <span className="h-3 bg-muted-foreground/10 w-16 shrink-0 rounded-full"/>}
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="flex shrink-0 items-center justify-center gap-4 py-5 text-sm text-contrast/40">
        <span>&copy; {new Date().getFullYear()} Neuroscale AI, Inc.</span>
        <Link href={NEUROSCALE_LINKS.terms} className="transition-colors hover:text-contrast/60">
          Terms of Service
        </Link>
        <Link href={NEUROSCALE_LINKS.privacy} className="transition-colors hover:text-contrast/60">
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
}
